/**
 * GEN-3199 — modo "sem header Authorization".
 *
 * Agentes de segurança em endpoints corporativos (caso Bradesco) bloqueiam a
 * requisição INTEIRA quando ela carrega o header `Authorization`: a chamada não
 * sai da máquina (falha em ~2ms, sem resposta HTTP) e a plataforma fica
 * inutilizável — não só o login, já que toda chamada autenticada leva o header.
 *
 * Quando isso é detectado, passamos a omitir o header: o token viaja no cookie
 * `token` (enviado automaticamente por ser mesma origem) e o proxy nginx
 * reconstrói o `Authorization` para o backend — ver nginx/default.conf.template.
 *
 * ⚠️ GATE OBRIGATÓRIO: só vale quando a API é servida na MESMA origem (base
 * relativa, ex. `/api`). Em stacks com API absoluta (ex.: Statkraft,
 * `https://prd.taxly.energycorp.com:3020`) não existe proxy para derivar o
 * header — omiti-lo ali quebraria a autenticação.
 */

const STORAGE_KEY = "taxly_auth_header_blocked";

// Respaldo em memória: se o ambiente também bloquear o sessionStorage, o modo
// continua válido pelo tempo de vida da página.
let blockedInMemory = false;

/** A API é servida na mesma origem? (única situação com proxy para derivar o header) */
export const isSameOriginApi = (): boolean => (process.env.REACT_APP_TAXLY_API || "").startsWith("/");

/** O header já foi detectado como bloqueado nesta sessão? */
export const isAuthHeaderBlocked = (): boolean => {
  if (!isSameOriginApi()) return false;
  if (blockedInMemory) return true;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

/** Registra a detecção — as chamadas seguintes já saem sem o header. */
export const markAuthHeaderBlocked = (): void => {
  if (!isSameOriginApi()) return;
  blockedInMemory = true;
  try {
    sessionStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* storage indisponível — o respaldo em memória basta */
  }
};

/** Limpa a detecção (logout e testes). */
export const resetAuthHeaderBlocked = (): void => {
  blockedInMemory = false;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};
